import type { OnBoardingStore } from "@/Modules/OnBoarding/Store";
import type { Choice } from "@/Modules/Models/Domain/OnBoarding";
import type { UserStore } from "@/Modules/User/Store";
import {
  AnswersIds,
  YesOrNoAnswer,
  StepIdWithChoices,
  SUBAREA_NAME_THERAPIST_KNOWN,
  AREA_NAME_COACHING,
} from "@/Modules/Models/Domain/OnBoarding";

import useUserStore from "@/Modules/User/Store";
import { navigatorLang } from "@/Utils/Language";

class SetAnswerActionHandler {
  constructor(
    private readonly onBoardingStore: OnBoardingStore,
    private readonly userStore: UserStore = useUserStore()
  ) {}

  public handle(answerId: AnswersIds | string, value: string | number | boolean) {
    if (answerId && value) {
      this.onBoardingStore.answers[answerId] = value;
    }

    if (answerId === AnswersIds.LANGUAGE && typeof value === "string") {
      this.onBoardingStore.updateLanguage(value);
      this.onBoardingStore.resetTherapyLanguage();
      this.onBoardingStore.resetTherapyType();
    }

    if (answerId === AnswersIds.USERNAME && typeof value === "string") {
      this.onBoardingStore.username = value;

      if (this.userStore.isSSOPatient) {
        this.onBoardingStore.setOptionsData(this.userStore.user?.language ?? navigatorLang);
      }
    }

    if (answerId === AnswersIds.THERAPY_LANGUAGE && typeof value === "string") {
      this.onBoardingStore.answers[AnswersIds.THERAPY_LANGUAGE] = value;
      const language = this.onBoardingStore.getAdditionalLanguageSelected?.extra ?? navigatorLang;

      this.onBoardingStore.answers[AnswersIds.LANGUAGE] = language;
      this.onBoardingStore.updateLanguage(language);
    }

    if (answerId === AnswersIds.THERAPY_TYPE && typeof value === "string") {
      this.onBoardingStore.areaSelected = "";
      delete this.onBoardingStore.answers[AnswersIds.MAIN_AREA];
      delete this.onBoardingStore.answers[AnswersIds.SUB_AREA];

      if (this.onBoardingStore.isCoachingSelected) {
        const area = this.onBoardingStore.getChoiceInfo(
          StepIdWithChoices.MAIN_AREA,
          AREA_NAME_COACHING,
          "name"
        );

        this.onBoardingStore.areaSelected = area?.uuid as string;
        this.onBoardingStore.resetTherapyLanguage();
      }
    }

    if (answerId === AnswersIds.MAIN_AREA && typeof value === "string") {
      const subArea = this.getSubAreas(
        value,
        this.onBoardingStore.getStepChoices(StepIdWithChoices.SUB_AREA)
      );

      this.onBoardingStore.areaSelected = value;

      if (subArea && subArea.name === SUBAREA_NAME_THERAPIST_KNOWN) {
        this.onBoardingStore.answers.sub_area = subArea.uuid;
      }
    }

    if (answerId === "email" && typeof value === "string") {
      this.onBoardingStore.email = value;
    }

    if (answerId === AnswersIds.GO_TO_LAST_THERAPIST) {
      this.onBoardingStore.patientWantsLastTherapist = value === YesOrNoAnswer.YES;
    }
  }

  private getSubAreas(areaUuid: string, areas: Choice[]) {
    return areas.find((area) => area.mainArea === areaUuid);
  }
}

export default SetAnswerActionHandler;
